import { Spinner } from '@cimplico/components';
import { useState } from 'react';
import XeroErrorAlert from '../components/XeroErrorAlert';
import ActiveInternalComments from '../features/dashboard/ActiveInternalComments';
import CheckedOutFiles from '../features/dashboard/CheckedOutFiles';
import ClientQueriesChart from '../features/dashboard/ClientQueriesChart';
import DashboardOverview from '../features/dashboard/DashboardOverview';
import DraftJournals from '../features/dashboard/DraftJournals';
import InternalCommentsChart from '../features/dashboard/InternalCommentsChart';
import JournalsChart from '../features/dashboard/JournalsChart';
import OtherSchedulesChart from '../features/dashboard/OtherSchedulesChart';
import OutstandingClientQueries from '../features/dashboard/OutstandingClientQueries';
import ProceduresChart from '../features/dashboard/ProceduresChart';
import WorkpaperProgressInsight from '../features/dashboard/WorkpaperProgressInsight';
import WorksheetsAssigned from '../features/dashboard/WorksheetsAssigned';
import WorksheetsChart from '../features/dashboard/WorksheetsChart';
import { useFirmContext } from '../providers/FirmProvider';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useWorkpaper } from '../queries/use-workpaper';
import { useWorkpaperProgress } from '../queries/use-workpaper-progress';
import { ClientQueryStatusEnum } from '../types/client-query';
import ErrorSplashScreen from './ErrorSplashScreen';

function DashboardPage(): JSX.Element {
    const { id, entity } = useWorkpaperContext();
    const { shortId } = useFirmContext();
    const workpaperQuery = useWorkpaper(id);
    const workpaperProgressQuery = useWorkpaperProgress(id);

    const [xeroError, setXeroError] = useState<{ name: string; message: string } | undefined>();

    if (workpaperQuery.isLoading || workpaperProgressQuery.isLoading) {
        return (
            <div className="absolute top-1/2 left-1/2">
                <Spinner />
            </div>
        );
    }

    if (workpaperQuery.isError || !workpaperQuery.isSuccess) {
        if (workpaperQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Workpaper not found."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/workpapers`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to workpapers"
                redirectUrl={`/${shortId}/workpapers`}
                leftArrow={true}
            />
        );
    }

    if (workpaperProgressQuery.isError || !workpaperProgressQuery.isSuccess) {
        if (workpaperProgressQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Workpaper not found."
                    redirectTitle="Back to workpapers"
                    redirectUrl={`/${shortId}/workpapers`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to workpapers"
                redirectUrl={`/${shortId}/workpapers`}
                leftArrow={true}
            />
        );
    }

    const { workpaper } = workpaperQuery.data;
    const { workpaperProgress } = workpaperProgressQuery.data;

    return (
        <>
            <div className="pb-4">
                <DashboardOverview workpaper={workpaper} />
            </div>

            {xeroError && (
                <div className="w-fit mx-auto mb-8">
                    <XeroErrorAlert
                        message={xeroError.message}
                        errorName={xeroError.name}
                        entityName={entity?.name}
                        entityId={entity?.id}
                    />
                </div>
            )}

            {workpaperProgress.progressBreakdown && (
                <div
                    data-intercom-target="workpaper-dashboard"
                    className="grid justify-items-center md:justify-items-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-2"
                >
                    <ProceduresChart progress={workpaperProgress.progressBreakdown.procedures} />
                    <WorksheetsChart progress={workpaperProgress.progressBreakdown.worksheets} />
                    <OtherSchedulesChart progress={workpaperProgress.progressBreakdown.otherSchedules} />
                    <JournalsChart progress={workpaperProgress.progressBreakdown.journals} />
                    <InternalCommentsChart progress={workpaperProgress.progressBreakdown.internalComments} />
                    <ClientQueriesChart progress={workpaperProgress.progressBreakdown.clientQueries} />
                </div>
            )}

            <div className="grid grid-cols-1 mt-8 gap-16 pb-8 lg:grid-cols-2">
                <WorksheetsAssigned />
                <WorkpaperProgressInsight workpaperProgress={workpaperProgress} />
                <ActiveInternalComments />
                <OutstandingClientQueries status={ClientQueryStatusEnum.ACTIVE} />
                <OutstandingClientQueries status={ClientQueryStatusEnum.DRAFT} />
                <DraftJournals onXeroError={setXeroError} />
                <CheckedOutFiles />
            </div>
        </>
    );
}
export default DashboardPage;
